import { UnorderedListOutlined } from "@ant-design/icons"
import { Button, Popover, Typography } from "antd"
import dayjs from "dayjs"
import { type DragEvent, type MouseEvent } from "react"

import Flex from "#blocks/Flex"
import ArtworkEditableThumb, {
  ARTWORK_EDIT_SOURCE,
} from "#components/ArtworkTiles/ArtworkEditableThumb"

import AddStepButton from "./AddStepButton"
import { selectVisibleTimelineStages } from "./portfolioPhotoGridHelpers"
import { DragHandle } from "./portfolioPhotoGridStyles"
import PriorityBadge from "./PriorityBadge"
import SquareArtThumb from "./SquareArtThumb"
import StepIconButton from "./StepIconButton"
import {
  PORTFOLIO_MAX_STEPS_PER_PROJECT,
  type PortfolioMockProject,
  type PortfolioPlanningProjectSummary,
} from "./usePortfolioDbStorage"

const { Text } = Typography

interface ProjectRowProps {
  studentId?: string | null
  project: PortfolioMockProject
  planningSummary: PortfolioPlanningProjectSummary | undefined
  coverRotation: number
  onSelectStage: (stageId: string) => void
  onAddStep: () => void
  onDragHandleStart: (event: DragEvent<HTMLSpanElement>) => void
  onDragHandleEnd: () => void
  /** Optional priority indicator badge top-right. */
  priorityIndex?: number
  /** Optional remove-X next to the badge (priority lane only). */
  onRemoveFromPriority?: () => void
}

const ProjectRow = ({
  studentId,
  project,
  planningSummary,
  coverRotation,
  onSelectStage,
  onAddStep,
  onDragHandleStart,
  onDragHandleEnd,
  priorityIndex,
  onRemoveFromPriority,
}: ProjectRowProps) => {
  const coverStage = [...project.stages]
    .reverse()
    .find((stage) => stage.imageUrl?.trim())
  const thumbnailUrl = coverStage?.imageUrl || project.coverImageUrl
  const soonestDueLabel = planningSummary?.soonestStepDueDate
    ? dayjs(planningSummary.soonestStepDueDate).format("MMM D")
    : "No due date"
  const orderedStages = project.stages
  const visibleStages = selectVisibleTimelineStages(orderedStages, 5)
  const visibleStageIdSet = new Set(visibleStages.map((stage) => stage.id))
  const overflowStages = orderedStages.filter(
    (stage) => !visibleStageIdSet.has(stage.id)
  )
  const stepLimitReached =
    project.stages.length >= PORTFOLIO_MAX_STEPS_PER_PROJECT

  const handleStepClick =
    (stageId: string) => (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      onSelectStage(stageId)
    }

  return (
    <Flex
      gap={8}
      style={{
        width: "100%",
        alignItems: "center",
        position: "relative",
      }}
    >
      <DragHandle
        draggable
        onClick={(event) => event.stopPropagation()}
        onDragStart={onDragHandleStart}
        onDragEnd={onDragHandleEnd}
        title="Drag to reorder project"
      />
      {studentId && coverStage?.artworkId ? (
        <ArtworkEditableThumb
          studentId={studentId}
          artworkId={coverStage.artworkId}
          source={ARTWORK_EDIT_SOURCE.PORTFOLIO}
          size={106}
          borderRadius={6}
          objectFit="cover"
          previewUrl={thumbnailUrl}
          previewRotation={coverRotation}
          alt={project.title}
          showTileActions={false}
        />
      ) : (
        <SquareArtThumb
          src={thumbnailUrl}
          alt={project.title}
          size={106}
          borderRadius={6}
          rotation={coverRotation}
        />
      )}
      <Flex col gap={2} style={{ minWidth: 0, flex: 1 }}>
        <Text strong ellipsis style={{ fontSize: 14 }}>
          {project.title}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Soonest: {soonestDueLabel}
        </Text>
        <Flex
          gap={0}
          style={{
            alignItems: "center",
            flexWrap: "wrap",
            overflowX: "auto",
            maxWidth: 260,
          }}
        >
          {visibleStages.map((stage) => (
            <StepIconButton
              key={stage.id}
              stage={stage}
              onClick={handleStepClick(stage.id)}
              tightPadding
            />
          ))}
          {overflowStages.length > 0 ? (
            <Popover
              trigger="click"
              content={
                <Flex
                  gap={6}
                  style={{
                    alignItems: "center",
                    flexWrap: "nowrap",
                    maxWidth: 420,
                    overflowX: "auto",
                  }}
                >
                  {orderedStages.map((stage) => (
                    <StepIconButton
                      key={`${project.id}-popover-${stage.id}`}
                      stage={stage}
                      onClick={handleStepClick(stage.id)}
                    />
                  ))}
                </Flex>
              }
            >
              <Button
                type="text"
                size="small"
                icon={<UnorderedListOutlined />}
                onClick={(event) => event.stopPropagation()}
                style={{ paddingInline: 4 }}
                title="View all project steps"
              />
            </Popover>
          ) : null}
          <AddStepButton
            onAdd={onAddStep}
            disabled={stepLimitReached}
            disabledReason={
              stepLimitReached
                ? `Step limit reached (${PORTFOLIO_MAX_STEPS_PER_PROJECT})`
                : undefined
            }
          />
        </Flex>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {project.stages.length} steps
        </Text>
      </Flex>
      <PriorityBadge
        priorityIndex={priorityIndex}
        onRemoveFromPriority={onRemoveFromPriority}
      />
    </Flex>
  )
}

export default ProjectRow
